import React, { useState } from 'react';
import { WizardData, SUPPORTED_LANGUAGES, LLM_MODELS } from '../types';

interface LanguageModelStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const LanguageModelStep: React.FC<LanguageModelStepProps> = ({ 
  data, 
  updateData, 
  onNext, 
  onPrevious 
}) => {
  const [additionalLanguagesExpanded, setAdditionalLanguagesExpanded] = useState(false);

  const handleModelChange = (modelId: string, provider: 'google' | 'openai' | 'anthropic') => {
    updateData({ 
      llmModel: modelId,
      llmProvider: provider
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (data.primaryLanguage && data.llmModel) {
      onNext();
    }
  };

  // (removed unused allModels)

  const toggleAdditionalLanguage = (langCode: string) => {
    const current = data.additionalLanguages || [];
    const updated = current.includes(langCode)
      ? current.filter(code => code !== langCode)
      : [...current, langCode];
    updateData({ additionalLanguages: updated });
  };

  const selectedAdditionalLanguages = data.additionalLanguages || [];
  const availableLanguages = SUPPORTED_LANGUAGES.filter(lang => lang.code !== data.primaryLanguage);

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">Language & AI Model</h3>
        <p className="text-gray-400">Configure language support and AI capabilities</p>
      </div>

      {/* Primary Language */}
      <div className="space-y-6">
        <h4 className="text-xl font-semibold text-white flex items-center">
          <span className="mr-2">🌍</span> Primary Language
        </h4>
        
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => updateData({ primaryLanguage: lang.code })}
              className={`
                p-3 rounded-lg border transition-all
                ${data.primaryLanguage === lang.code
                  ? 'border-blue-500 bg-blue-600/20 text-white'
                  : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-blue-400 hover:bg-gray-700'
                }
              `}
              title={lang.name}
            >
              <div className="text-2xl mb-1">{lang.flag}</div>
              <div className="text-xs">{lang.code.toUpperCase()}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Additional Languages - Compact */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">Additional Languages (Optional)</h4>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setAdditionalLanguagesExpanded(!additionalLanguagesExpanded)}
            className="flex items-center justify-between w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
          >
            <span className="text-sm">
              {selectedAdditionalLanguages.length === 0 
                ? 'Select additional languages...' 
                : `${selectedAdditionalLanguages.length} language${selectedAdditionalLanguages.length > 1 ? 's' : ''} selected`
              }
            </span>
            <span className={`transform transition-transform ${additionalLanguagesExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>

          {/* Selected Languages Display */}
          {selectedAdditionalLanguages.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedAdditionalLanguages.map(langCode => {
                const lang = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
                if (!lang) return null;
                return (
                  <span
                    key={langCode}
                    className="inline-flex items-center px-3 py-1 bg-blue-600/20 text-blue-300 text-sm rounded-full border border-blue-600"
                  >
                    <span className="mr-1">{lang.flag}</span>
                    {lang.name}
                    <button
                      type="button"
                      onClick={() => toggleAdditionalLanguage(langCode)}
                      className="ml-2 text-blue-400 hover:text-blue-200"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Dropdown */}
          {additionalLanguagesExpanded && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-xl p-4 z-10 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-1 gap-2">
                {availableLanguages.map((lang) => {
                  const isSelected = selectedAdditionalLanguages.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => toggleAdditionalLanguage(lang.code)}
                      className={`
                        flex items-center px-3 py-2 rounded-lg text-left transition-all
                        ${isSelected
                          ? 'bg-blue-600/20 text-blue-300 border border-blue-600'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }
                      `}
                    >
                      <span className="text-lg mr-3">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                      {isSelected && (
                        <span className="ml-auto text-blue-400">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* LLM Model Selection */}
      <div className="space-y-6">
        <h4 className="text-xl font-semibold text-white flex items-center">
          <span className="mr-2">🧠</span> AI Model
        </h4>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-600 max-h-96 overflow-y-auto">
          {Object.entries(LLM_MODELS).map(([providerKey, models]) => (
            <div key={providerKey} className="mb-3">
              <h5 className="text-xs text-gray-400 font-semibold mb-2 uppercase flex items-center">
                {providerKey === 'openai' && '⚪'}{providerKey === 'google' && '🔵'}{providerKey === 'anthropic' && '🟣'}
                <span className="ml-1">{providerKey}</span>
              </h5>
              <div className="space-y-1">
                {models.map((model) => (
                  <div
                    key={model.id}
                    onClick={() => handleModelChange(model.id, providerKey as 'google' | 'openai' | 'anthropic')}
                    className={`
                      rounded-lg px-3 py-2 cursor-pointer transition-all flex items-center justify-between
                      ${data.llmModel === model.id
                        ? 'bg-blue-600/20 text-white border border-blue-500'
                        : 'hover:bg-gray-700 text-gray-300 border border-transparent'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <span className="mr-2 text-sm">{model.icon}</span>
                      <div>
                        <span className="text-sm font-medium">{model.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{model.description}</span>
                      </div>
                    </div>
                    {data.llmModel === model.id && (
                      <span className="text-blue-400 text-sm">✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Temperature Setting */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-white">Temperature</h4>
          <span className="text-blue-400 font-mono">{data.temperature}</span>
        </div>
        
        <div className="space-y-3">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={data.temperature}
            onChange={(e) => updateData({ temperature: parseFloat(e.target.value) })}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
          
          <div className="flex justify-between text-xs text-gray-400">
            <span>0.0 - Focused</span>
            <span>0.5 - Balanced</span>
            <span>1.0 - Creative</span>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-600">
            <p className="text-sm text-gray-300">
              <strong>Temperature</strong> controls randomness in responses. 
              Lower values (0.0-0.3) make responses more focused and deterministic, 
              while higher values (0.7-1.0) make them more creative and varied.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onPrevious}
          className="bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
        >
          ← Previous
        </button>
        <button
          type="submit"
          disabled={!data.primaryLanguage || !data.llmModel}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          Next Step →
        </button>
      </div>
    </form>
  );
};

export default LanguageModelStep;